import { z } from "zod";
import { CONVERSATION_EVENT_ACTOR_TYPES, CONVERSATION_WORKFLOW_STATUSES, CONVERSATION_EVENT_REASONS, CONVERSATION_EVENT_TYPES, CONVERSATION_STATUSES, MESSAGE_STATUSES, MESSAGE_TYPES, ORGANIZATION_MEMBER_ROLES, PLATFORM_TYPES, MESSAGE_ORIGINS, } from "./constants.js";
import { contactPlatformProfileSummaryDtoSchema } from "./contact-state.js";
import { apiBigIntIdSchema, apiIntIdSchema } from "./ids.js";
import { messageContactPresentationDtoSchema, messageInteractivePresentationDtoSchema, messageLocationPresentationDtoSchema, messageSharePresentationDtoSchema, messageStoryReplyPresentationDtoSchema, messageUnsupportedPresentationDtoSchema, } from "./whatsapp-inbound-presentation.js";
import { messageOriginatingSocialCommentDtoSchema } from "./social-comments-contracts.js";
import { whatsAppTemplatePresentationSchema } from "./whatsapp-template-presentation.js";
/**
 * HTTP data-transfer objects. Prisma records are never returned directly: a
 * presenter converts them so BigInt identifiers become decimal strings and no
 * credential or provider payload can leak into a response by accident.
 */
export const organizationMemberDtoSchema = z.object({
    memberId: apiIntIdSchema,
    organizationId: apiIntIdSchema,
    userId: apiIntIdSchema,
    role: z.enum(ORGANIZATION_MEMBER_ROLES),
    status: z.enum(["ACTIVE", "SUSPENDED"]),
});
/** Assignee summary for list/detail. Null when unassigned. */
export const conversationAssignedMemberDtoSchema = z.object({
    memberId: apiIntIdSchema,
    displayName: z.string().min(1),
    /** False when the member is soft-deleted or SUSPENDED. */
    isActive: z.boolean(),
});
/** Shared conversation fields for list and details (without derived CS window). */
const conversationCoreDtoSchema = z.object({
    conversationId: apiIntIdSchema,
    contactPlatformProfileId: apiIntIdSchema,
    platformEndpointId: apiIntIdSchema,
    /**
     * Persisted `PlatformAccount.platformType` for this conversation's profile account.
     * Not inferred from phone, username, endpoint id, or message content.
     */
    platformType: z.enum(PLATFORM_TYPES),
    status: z.enum(CONVERSATION_STATUSES),
    assignedMemberId: apiIntIdSchema.nullable(),
    /**
     * Resolved assignee for UI. Null when unassigned.
     * 43F1C.1 returns null until assignment writers ship; clients should prefer this over id-only.
     */
    assignedMember: conversationAssignedMemberDtoSchema.nullable(),
    closedAt: z.string().nullable(),
    contactPlatformProfile: contactPlatformProfileSummaryDtoSchema,
});
/**
 * Conversation details. Includes derived inbound/CS-window fields for the
 * Inbox composer and Conversation header. Not present on list items.
 */
export const FREEFORM_SEND_BLOCKED_REASONS = [
    "WINDOW_CLOSED",
    "CHANNEL_UNSUPPORTED",
];
export const conversationDtoSchema = conversationCoreDtoSchema.extend({
    /**
     * Derived: COALESCE(platformSentAt, createdAt) of the latest inbound Message
     * (`organizationMemberId` IS NULL). Null when no inbound message exists.
     * Platform-agnostic conversation fact — not WhatsApp-only.
     */
    lastInboundCustomerMessageAt: z.string().nullable(),
    /**
     * Derived: latest inbound Message timestamp + 24 hours.
     * Null when there is no qualifying inbound message.
     * Product use is **WhatsApp** free-form gating only. Do not reinterpret as
     * Messenger policy — use `canSendFreeformText` for Facebook.
     */
    customerServiceWindowExpiresAt: z.string().nullable(),
    /**
     * Backend-authoritative: whether the current member may create a free-form
     * text outbound Message on this conversation right now.
     * WhatsApp: open customer-service window. Facebook: open Messenger RESPONSE
     * window. Instagram: always false.
     */
    canSendFreeformText: z.boolean(),
    /**
     * Stable reason when `canSendFreeformText` is false; null when send is allowed.
     */
    freeformSendBlockedReason: z.enum(FREEFORM_SEND_BLOCKED_REASONS).nullable(),
    /**
     * Present when a SocialComment.privateReplyConversationId links this conversation
     * to a verified Private Reply origin. Never fabricated from author matching alone.
     */
    originatingSocialComment: messageOriginatingSocialCommentDtoSchema.nullable().optional(),
});
/** Derived from the latest Message; not stored as Conversation columns. */
export const conversationLastMessagePreviewDtoSchema = z.object({
    content: z.string().nullable(),
    messageType: z.enum(MESSAGE_TYPES),
    status: z.enum(MESSAGE_STATUSES),
    organizationMemberId: apiIntIdSchema.nullable(),
    origin: z.enum(MESSAGE_ORIGINS).nullable().optional(),
    direction: z.enum(["INBOUND", "OUTBOUND"]).optional(),
    /** Safe WhatsApp unsupported subtype for list preview copy. */
    unsupportedKind: messageUnsupportedPresentationDtoSchema.shape.kind.optional(),
    /** WhatsApp Cloud API voice note. Omitted for ordinary audio files. */
    providerVoice: z.boolean().optional(),
});
export const conversationListItemDtoSchema = conversationCoreDtoSchema.extend({
    lastMessageAt: z.string().nullable(),
    lastMessagePreview: conversationLastMessagePreviewDtoSchema.nullable(),
    /**
     * Derived: customer inbound after watermark, or at/after manualUnreadMessageId hold.
     */
    unreadCount: z.number().int().nonnegative(),
});
export const conversationListResponseSchema = z.object({
    items: z.array(conversationListItemDtoSchema),
    nextCursor: z.string().nullable(),
});
/** Organization-scoped unread conversation counter (P1 / C-7c). Counts conversations, not messages. */
export const inboxUnreadSummaryResponseSchema = z.object({
    unreadConversationCount: z.number().int().nonnegative(),
});
/** Members eligible for conversation assignment. */
export const assignableMemberDtoSchema = z.object({
    memberId: apiIntIdSchema,
    displayName: z.string().min(1),
    role: z.enum(ORGANIZATION_MEMBER_ROLES),
    isCurrentMember: z.boolean(),
});
export const assignableMembersResponseSchema = z.object({
    items: z.array(assignableMemberDtoSchema),
});
/** Persisted ConversationEvent row for Inbox timeline merge. */
export const conversationTimelineEventDtoSchema = z.object({
    eventId: apiBigIntIdSchema,
    conversationId: apiIntIdSchema,
    eventType: z.enum(CONVERSATION_EVENT_TYPES),
    actorType: z.enum(CONVERSATION_EVENT_ACTOR_TYPES),
    /** Localized display label resolved at read time (member name or "FeedBot team"). */
    actorLabel: z.string().min(1),
    fromAssignedMember: conversationAssignedMemberDtoSchema.nullable(),
    toAssignedMember: conversationAssignedMemberDtoSchema.nullable(),
    reason: z.enum(CONVERSATION_EVENT_REASONS).nullable(),
    occurredAt: z.string(),
});
/** Mark org-shared conversation read through a loaded inbound message id. */
export const markConversationReadRequestSchema = z.object({
    throughMessageId: apiBigIntIdSchema,
});
export const markConversationReadResponseSchema = z.object({
    conversationId: apiIntIdSchema,
    lastReadInboundMessageId: apiBigIntIdSchema,
    unreadCount: z.number().int().nonnegative(),
});
export const updateConversationAssignmentRequestSchema = z.object({
    assignedMemberId: apiIntIdSchema.nullable(),
});
export const updateConversationStatusRequestSchema = z.object({
    status: z.enum(CONVERSATION_WORKFLOW_STATUSES),
});
export const markConversationUnreadResponseSchema = z.object({
    conversationId: apiIntIdSchema,
    unreadCount: z.number().int().nonnegative(),
});
export const CONVERSATION_EXPORT_FORMATS = ["csv", "xlsx"];
export const conversationExportQuerySchema = z.object({
    format: z.enum(CONVERSATION_EXPORT_FORMATS),
    /** Sheet direction only. Row text is the same in both formats. */
    locale: z.enum(["en", "he"]).optional(),
});
/** Spreadsheet data-row cap (header is extra). Above this, export returns an error and no file. */
export const CONVERSATION_EXPORT_MAX_DATA_ROWS = 1_048_575;
/** Safe inbound media attachment for Inbox rendering (provider CDN URL; never tokens). */
export const messageAttachmentDtoSchema = z.object({
    attachmentId: apiBigIntIdSchema,
    attachmentType: z.string().min(1),
    mimeType: z.string().nullable(),
    /** https CDN URL when persisted from Meta image/GIF webhooks; omitted when unavailable. */
    url: z.string().url().optional(),
    fileName: z.string().nullable().optional(),
    fileSizeBytes: z.string().nullable().optional(),
    storageState: z
        .enum(["METADATA_ONLY", "PENDING_STORAGE", "STORED", "UNAVAILABLE", "EXPIRED"])
        .optional(),
    /**
     * WhatsApp Cloud API voice note (`audio.voice === true`).
     * Omitted for ordinary audio files. Never a new MessageType.
     */
    providerVoice: z.boolean().optional(),
    /**
     * Recorder-measured duration for recorded voice notes.
     * Omitted for ordinary uploaded audio that has no recorded duration.
     */
    durationSeconds: z.number().positive().optional(),
});
/** `messageId` is BigInt-backed, so it crosses the wire as a decimal string. */
export const messageReplyContextDtoSchema = z.object({
    /**
     * Referenced Multi-Chat message id when resolved in the same conversation.
     * Null when Meta context was persisted but the target is unavailable.
     */
    messageId: apiBigIntIdSchema.nullable(),
    direction: z.enum(["INBOUND", "OUTBOUND"]).optional(),
    /**
     * Safe display name for the quoted message author (contact/platform or sending member).
     * Omitted when the target is unavailable or no trusted label can be derived.
     */
    authorLabel: z.string().min(1).optional(),
    content: z.string().nullable().optional(),
    messageType: z.enum(MESSAGE_TYPES).optional(),
    /** Immutable template display when the quoted message is a v2 template send. */
    templatePresentation: whatsAppTemplatePresentationSchema.optional(),
    /**
     * First image/sticker attachment on the quoted message for compact reply thumbnails.
     * Omitted when unavailable or not an image-like attachment.
     */
    previewAttachment: messageAttachmentDtoSchema.optional(),
    contactPresentation: messageContactPresentationDtoSchema.optional(),
    locationPresentation: messageLocationPresentationDtoSchema.optional(),
    sharePresentation: messageSharePresentationDtoSchema.optional(),
    unsupportedPresentation: messageUnsupportedPresentationDtoSchema.optional(),
});
/** Short-lived presigned download capability for stored attachments. */
export const messageAttachmentDownloadUrlRequestSchema = z.object({
    /** `inline` for display; `attachment` triggers browser download via signed ResponseContentDisposition. */
    disposition: z.enum(["inline", "attachment"]).optional().default("inline"),
});
export const messageAttachmentDownloadUrlResponseSchema = z.object({
    url: z.string().url(),
    expiresAt: z.string(),
});
/** Short-lived authorized IMAGE header for a sent WhatsApp template message. */
export const messageTemplateHeaderImageDownloadUrlResponseSchema = z.object({
    url: z.string().url(),
    expiresAt: z.string(),
});
export const messageDtoSchema = z.object({
    messageId: apiBigIntIdSchema,
    conversationId: apiIntIdSchema,
    organizationMemberId: apiIntIdSchema.nullable(),
    origin: z.enum(MESSAGE_ORIGINS).nullable().optional(),
    direction: z.enum(["INBOUND", "OUTBOUND"]).optional(),
    content: z.string().nullable(),
    messageType: z.enum(MESSAGE_TYPES),
    status: z.enum(MESSAGE_STATUSES),
    /**
     * Sanitized outbound failure code from the latest FAILED MessageStatusEvent.
     * Present only when stored status is FAILED. Never includes provider stderr.
     */
    failureCode: z.string().optional(),
    clientIdempotencyKey: z.uuid().nullable(),
    platformSentAt: z.string().nullable(),
    createdAt: z.string(),
    /**
     * Immutable outbound WhatsApp template display model (v2 sends).
     * Derived from Message.structuredData presentation — never live WhatsAppTemplate.
     */
    templatePresentation: whatsAppTemplatePresentationSchema.optional(),
    /**
     * Quoted/reply target resolved from provider context / reply_to mid at read time.
     * Present only when structuredData includes reply context metadata.
     */
    replyContext: messageReplyContextDtoSchema.optional(),
    /** Inbound media attachments (e.g. Meta image/GIF). Soft-deleted rows excluded. */
    attachments: z.array(messageAttachmentDtoSchema).optional(),
    contactPresentation: messageContactPresentationDtoSchema.optional(),
    locationPresentation: messageLocationPresentationDtoSchema.optional(),
    sharePresentation: messageSharePresentationDtoSchema.optional(),
    storyReplyPresentation: messageStoryReplyPresentationDtoSchema.optional(),
    unsupportedPresentation: messageUnsupportedPresentationDtoSchema.optional(),
    interactivePresentation: messageInteractivePresentationDtoSchema.optional(),
    /**
     * Present only when Message.originatingSocialCommentId points at a real SocialComment
     * (Private Reply path). Never fabricated for ordinary DMs.
     */
    originatingSocialComment: messageOriginatingSocialCommentDtoSchema.optional(),
});
export const messageListResponseSchema = z.object({
    items: z.array(messageDtoSchema),
    nextCursor: z.string().nullable(),
    /**
     * ConversationEvent rows whose occurredAt falls in this messages page window.
     * Old clients ignore the field. Empty when the conversation has no events.
     */
    events: z.array(conversationTimelineEventDtoSchema).default([]),
});
/**
 * One logical client send action. The client generates the UUID.
 * conversationId lives only in the URL path for POST .../messages.
 * At least one of content (trimmed) or attachmentIds is required.
 * Optional replyToMessageId is a local Message id (Facebook / WhatsApp outbound native reply).
 * Instagram Login rejects outbound replyToMessageId (provider Send API limitation).
 */
export const createMessageRequestSchema = z
    .object({
    clientIdempotencyKey: z.uuid(),
    content: z.string().max(4096).optional(),
    attachmentIds: z.array(apiBigIntIdSchema).max(1).optional(),
    replyToMessageId: apiBigIntIdSchema.optional(),
})
    .superRefine((value, ctx) => {
    const trimmed = (value.content ?? "").trim();
    const hasAttachment = (value.attachmentIds?.length ?? 0) > 0;
    if (trimmed.length === 0 && !hasAttachment) {
        ctx.addIssue({
            code: "custom",
            path: ["content"],
            message: "Message content or attachment is required",
        });
    }
});
/** Outbound attachment after browser upload (before send). */
export const outboundAttachmentUploadResponseSchema = z.object({
    attachmentId: apiBigIntIdSchema,
    attachmentType: z.string().min(1),
    mimeType: z.string().min(1),
    fileName: z.string().nullable(),
    fileSizeBytes: z.string().regex(/^\d+$/),
    storageState: z.enum([
        "METADATA_ONLY",
        "PENDING_STORAGE",
        "STORED",
        "UNAVAILABLE",
        "EXPIRED",
    ]),
    durationSeconds: z.number().positive().optional(),
});
export const outboundMediaCapabilitiesResponseSchema = z.object({
    platform: z.enum(["WHATSAPP", "FACEBOOK", "INSTAGRAM"]),
    maxAttachmentsPerSend: z.number().int().positive(),
    entries: z.array(z.object({
        attachmentType: z.string(),
        maxBytes: z.number().int().positive(),
        allowedMimeTypes: z.array(z.string()),
        captions: z.boolean(),
        filename: z.boolean(),
        replies: z.boolean(),
    })),
});
/**
 * Template send from a conversation (18.2).
 * Server resolves the local WhatsAppTemplate, validates ownership, and builds
 * Meta components. Clients supply placeholder values only — never raw Meta components.
 *
 * `parameterKey` is "1" / "2" for positional or "first_name" for named.
 * `componentType` + optional `buttonIndex` prevent HEADER/BODY/BUTTON key collisions.
 */
export const createTemplateMessageParameterSchema = z.object({
    componentType: z.enum(["HEADER", "BODY", "BUTTON"]),
    /** Required when componentType is BUTTON: index within the template BUTTONS array. */
    buttonIndex: z.number().int().nonnegative().optional(),
    parameterKey: z.string().min(1).max(64),
    value: z.string().min(1).max(1024),
});
export const createTemplateMessageRequestSchema = z
    .object({
    clientIdempotencyKey: z.uuid(),
    whatsappTemplateId: apiIntIdSchema,
    parameters: z.array(createTemplateMessageParameterSchema).max(50),
})
    .superRefine((value, ctx) => {
    for (const [index, parameter] of value.parameters.entries()) {
        if (parameter.componentType === "BUTTON" && parameter.buttonIndex === undefined) {
            ctx.addIssue({
                code: "custom",
                path: ["parameters", index, "buttonIndex"],
                message: "buttonIndex is required when componentType is BUTTON",
            });
        }
        if (parameter.componentType !== "BUTTON" && parameter.buttonIndex !== undefined) {
            ctx.addIssue({
                code: "custom",
                path: ["parameters", index, "buttonIndex"],
                message: "buttonIndex is only allowed when componentType is BUTTON",
            });
        }
    }
});
//# sourceMappingURL=dto.js.map